import React from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Button } from "./ui/button";
import { Building2, Plane, Route as RouteIcon, Check } from "lucide-react";
import { useI18n } from "../i18n";
import { useBooking } from "../lib/booking-context";

const IMAGES = {
  city: "https://images.unsplash.com/photo-1541849546-216549ae216d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  airport: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  long: "https://images.unsplash.com/photo-1617901883714-151853555aa8?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200",
};

export const Services = () => {
  const { t } = useI18n();
  const { requestBooking } = useBooking();

  const tabs = [
    { key: "city", icon: Building2, cat: "city", testid: "services-tab-city" },
    { key: "airport", icon: Plane, cat: "airport", testid: "services-tab-airport" },
    { key: "long", icon: RouteIcon, cat: "long_distance", testid: "services-tab-long-distance" },
  ];

  return (
    <section id="services" className="py-14 sm:py-18 lg:py-24 bg-secondary/40" data-testid="services-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-[-0.015em]">{t("services.title")}</h2>
          <p className="mt-3 text-muted-foreground">{t("services.subtitle")}</p>
        </div>

        <Tabs defaultValue="city" className="mt-8" data-testid="services-tabs">
          <TabsList className="grid grid-cols-3 w-full sm:w-auto sm:inline-grid rounded-xl bg-card border border-border p-1 h-auto">
            {tabs.map((tb) => (
              <TabsTrigger key={tb.key} value={tb.key} data-testid={tb.testid} className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground py-2.5 gap-2">
                <tb.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{t(`services.${tb.key}.name`)}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {tabs.map((tb) => (
            <TabsContent key={tb.key} value={tb.key} className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <div className="order-2 md:order-1">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-secondary">
                    <tb.icon className="h-5 w-5 text-primary" />
                  </span>
                  <h3 className="mt-4 font-display text-2xl font-semibold">{t(`services.${tb.key}.name`)}</h3>
                  <p className="mt-3 text-muted-foreground leading-relaxed">{t(`services.${tb.key}.desc`)}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {[t("hero.badge2"), t("hero.badge1"), t("benefits.items")[4]?.t].filter(Boolean).map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 text-sm rounded-full bg-secondary px-3 py-1">
                        <Check className="h-3.5 w-3.5 text-[hsl(var(--success))]" /> {f}
                      </span>
                    ))}
                  </div>
                  <Button className="mt-6 rounded-xl active:scale-[0.98]" onClick={() => requestBooking({ category: tb.cat })} data-testid={`services-book-${tb.key}`}>
                    {t(`services.${tb.key}.cta`)}
                  </Button>
                </div>
                <div className="order-1 md:order-2">
                  <div className="rounded-2xl overflow-hidden border border-border shadow-sm gold-glow">
                    <img src={IMAGES[tb.key]} alt={t(`services.${tb.key}.name`)} loading="lazy" decoding="async" className="w-full h-56 sm:h-72 object-cover" />
                  </div>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};
